/**
 * Phase 101fx (Feb 2026) — PartyDetailPanel : édition Party + backlinks vers sources.
 *
 * Fenêtre latérale (Sheet) affichant une Party et ses sources d'origine.
 * L'édition ici est LA source de vérité pour la Party ; aucune propagation
 * automatique vers les collections sources (vague 2/3 pour ça).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, User, Mail, Phone, MapPin, Pencil, Save, X, Loader2,
  Link as LinkIcon, StickyNote, GitMerge, History,
} from "lucide-react";
import { api } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { PartyMergeDialog } from "./PartyMergeDialog";

const MODULE_LABELS = {
  distributor: "Automate",
  reseller: "Revendeur",
  shop_customer: "Client B2C",
  catering_quote: "Devis traiteur",
  reseller_application: "Candidature",
  company_letter: "Courrier",
  standalone_contact: "Contact autonome",
};

const MODULE_LINKS = {
  distributor: (id) => `/distributeurs/${id}`,
  reseller: (id) => `/partenaires`,  // pas de detail page dédiée
  shop_customer: null,
  catering_quote: (id) => `/devis-traiteur/${id}`,
  reseller_application: null,
  company_letter: (id) => `/courriers/${id}`,
  standalone_contact: null,
};

export function PartyDetailPanel({ partyId, open, onClose, onSaved }) {
  const [party, setParty] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/parties/${partyId}`);
      setParty(data);
      setForm({
        display_name: data.display_name || "",
        legal_name: data.legal_name || "",
        email: data.email || "",
        phone: data.phone || "",
        address: data.address || "",
        postal_code: data.postal_code || "",
        city: data.city || "",
        country: data.country || "Suisse",
        notes: data.notes || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
      onClose?.();
    }
  };

  useEffect(() => { if (open && partyId) load(); /* eslint-disable-next-line */ }, [open, partyId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/parties/${partyId}`, form);
      const propagated = data?._propagated_to || [];
      const cascadeCount = propagated.filter((p) => p.fields_written?.length > 0).length;
      toast.success(
        cascadeCount
          ? `Party mise à jour · ${cascadeCount} source(s) synchronisée(s)`
          : "Party mise à jour"
      );
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!party) {
    return (
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent className="w-full sm:max-w-lg">
          <div className="flex items-center justify-center py-20 text-[#575A57]">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="party-detail-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {party.kind === "organization" ? <Building2 className="w-5 h-5 text-[#1F4E3D]" /> : <User className="w-5 h-5 text-[#1F4E3D]" />}
            {party.display_name}
          </SheetTitle>
          {party.legal_name && party.legal_name !== party.display_name && (
            <div className="text-sm text-[#575A57] pl-7">{party.legal_name}</div>
          )}
          {party.is_merged && (
            <div className="pl-7 mt-2">
              <Badge className="bg-[#8C3A2A] text-white text-xs">
                Fusionnée → {(party.merged_into || "").slice(0, 8)}…
              </Badge>
            </div>
          )}
        </SheetHeader>

        {/* Vague 4 — Actions de fusion */}
        {!party.is_merged && (
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-[#8C3A2A] text-[#8C3A2A] hover:bg-[#FDF3EA]"
              onClick={() => setMergeOpen(true)}
              data-testid="party-merge-btn"
            >
              <GitMerge className="w-3 h-3 mr-1" />
              Fusionner avec…
            </Button>
          </div>
        )}

        {/* Historique de fusion (Parties absorbées) */}
        {(party.merged_from || []).length > 0 && (
          <section className="mt-4 border border-[#E9D9B7] rounded-md p-3 bg-[#FBF4E9]">
            <h3 className="text-sm font-semibold text-[#8C6B2F] flex items-center gap-2 mb-2">
              <History className="w-4 h-4" /> Fusions absorbées ({party.merged_from.length})
            </h3>
            <div className="text-xs space-y-1">
              {party.merged_from.map((pid) => (
                <div key={pid} className="font-mono text-[#575A57]">
                  {pid.slice(0, 8)}…
                </div>
              ))}
            </div>
            {party.merged_at && (
              <div className="mt-2 text-[10px] text-[#A3A6A3]">
                Dernière fusion : {new Date(party.merged_at).toLocaleString("fr-CH")}
                {party.merged_by ? ` · par ${party.merged_by}` : ""}
              </div>
            )}
          </section>
        )}

        {/* Coordonnées */}
        <section className="mt-4 border border-[#E5E5E0] rounded-md p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#1A1C1A] flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#1F4E3D]" /> Coordonnées
            </h3>
            {!editing ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="party-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> Modifier
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); load(); }}>
                  <X className="w-3 h-3 mr-1" /> Annuler
                </Button>
                <Button size="sm" onClick={save} disabled={saving} className="bg-[#1F4E3D]" data-testid="party-save-btn">
                  <Save className="w-3 h-3 mr-1" /> {saving ? "…" : "Enregistrer"}
                </Button>
              </div>
            )}
          </div>

          {!editing ? (
            <div className="space-y-2 text-sm">
              {party.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-[#6B6E6B]" /> <a href={`mailto:${party.email}`} className="text-[#1F4E3D] hover:underline">{party.email}</a></div>}
              {party.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-[#6B6E6B]" /> <a href={`tel:${party.phone}`} className="hover:underline">{party.phone}</a></div>}
              {(party.address || party.city) && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-3 h-3 text-[#6B6E6B] mt-0.5" />
                  <div>
                    {party.address && <div>{party.address}</div>}
                    {(party.postal_code || party.city) && <div>{[party.postal_code, party.city].filter(Boolean).join(" ")} {party.country && party.country !== "Suisse" ? `· ${party.country}` : ""}</div>}
                  </div>
                </div>
              )}
              {party.notes && (
                <div className="mt-2 p-2 bg-[#FAFAF7] border-l-2 border-[#C1A063] text-xs italic text-[#575A57]">
                  <StickyNote className="w-3 h-3 inline mr-1" /> {party.notes}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Nom affiché</label>
                <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="party-edit-display_name" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Nom légal (organisation)</label>
                <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Email</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="party-edit-email" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Téléphone</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Adresse</label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Code postal</label>
                <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Ville</label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="party-edit-city" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Pays</label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Notes</label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
              </div>
            </div>
          )}
        </section>

        {/* Sources liées (backlinks) */}
        <section className="mt-4 border border-[#E5E5E0] rounded-md p-4 bg-white">
          <h3 className="text-sm font-semibold text-[#1A1C1A] flex items-center gap-2 mb-3">
            <LinkIcon className="w-4 h-4 text-[#1F4E3D]" /> Sources liées ({(party.linked_sources || []).length})
          </h3>
          {(party.linked_sources || []).length === 0 ? (
            <div className="text-xs text-[#A3A6A3] italic">Aucune source (Party créée manuellement).</div>
          ) : (
            <div className="space-y-2">
              {party.linked_sources.map((s, i) => {
                const linker = MODULE_LINKS[s.module];
                const url = linker ? linker(s.id) : null;
                return (
                  <div key={`${s.module}-${s.id}-${i}`} className="flex items-center justify-between text-sm p-2 rounded bg-[#FAFAF7]">
                    <div>
                      <Badge variant="outline" className="text-xs">{MODULE_LABELS[s.module] || s.module}</Badge>
                      <span className="ml-2 font-mono text-xs text-[#575A57]">{s.id.slice(0, 8)}…</span>
                    </div>
                    {url && (
                      <a href={url} className="text-xs text-[#1F4E3D] hover:underline">
                        Ouvrir →
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Meta */}
        <section className="mt-4 text-xs text-[#A3A6A3] space-y-1 pl-1">
          <div>ID : <span className="font-mono">{party.id}</span></div>
          <div>Dedup key : <span className="font-mono">{party.dedup_key}</span></div>
          {party.updated_at && <div>Dernière modif : {new Date(party.updated_at).toLocaleString("fr-CH")}</div>}
        </section>

        <PartyMergeDialog
          open={mergeOpen}
          onClose={() => setMergeOpen(false)}
          sourceParty={party}
          onMerged={() => { setMergeOpen(false); onSaved?.(); }}
        />
      </SheetContent>
    </Sheet>
  );
}
